import React, { useState } from 'react';
import Lock from 'lucide-react/dist/esm/icons/lock';
import Mail from 'lucide-react/dist/esm/icons/mail';
import KeyRound from 'lucide-react/dist/esm/icons/key-round';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import Eye from 'lucide-react/dist/esm/icons/eye';
import EyeOff from 'lucide-react/dist/esm/icons/eye-off';
import { forgotPassword, confirmForgotPassword } from '../services/auth';
import { getTranslation } from '../../../i18n';

const ForgotPasswordForm = ({ onBack, language = 'en' }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');

  const t = (key) => getTranslation(language, key);

  const calculatePasswordStrength = (pwd) => {
    if (!pwd) return '';
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (pwd.length >= 12) strength++;
    if (/[a-z]/.test(pwd)) strength++;
    if (/[A-Z]/.test(pwd)) strength++;
    if (/\d/.test(pwd)) strength++;
    if (/[^a-zA-Z\d]/.test(pwd)) strength++;
    if (strength <= 2) return 'weak';
    if (strength <= 4) return 'medium';
    return 'strong';
  };

  const handlePasswordChange = (value) => {
    setNewPassword(value);
    setPasswordStrength(calculatePasswordStrength(value));
  };

  const handleKeyPress = (e) => {
    const isCapsLock = e.getModifierState && e.getModifierState('CapsLock');
    setCapsLockOn(isCapsLock);
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const result = await forgotPassword(email);

    if (result.success) {
      setSuccess(result.message || t('resetCodeSent'));
      setStep(2);
    } else {
      setError(result.error || t('failedToSendCode'));
    }

    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      return;
    }

    if (newPassword.length < 8) {
      setError(t('passwordTooShort'));
      return;
    }

    setLoading(true);

    const result = await confirmForgotPassword(email, code, newPassword);

    if (result.success) {
      setSuccess(result.message || t('passwordResetSuccess'));
      setTimeout(() => onBack(), 2000);
    } else {
      setError(result.error || t('failedToResetPassword'));
    }

    setLoading(false);
  };

  const strengthColors = {
    weak: 'bg-gradient-to-r from-red-500 to-red-600',
    medium: 'bg-gradient-to-r from-amber-500 to-amber-600',
    strong: 'bg-gradient-to-r from-green-500 to-green-600'
  };

  const strengthTextColors = {
    weak: 'text-status-error',
    medium: 'text-amber-500',
    strong: 'text-green-500'
  };

  const strengthWidths = {
    weak: 'w-1/3',
    medium: 'w-2/3',
    strong: 'w-full'
  };

  return (
    <div className="relative h-full w-full flex justify-center items-center p-8">
      {/* Background glow effect */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none animate-pulse"
        style={{ background: 'radial-gradient(circle, rgba(33, 158, 188, 0.15) 0%, transparent 70%)' }}
      />

      <div className="relative z-10 w-full max-w-[450px] px-10 py-7 rounded-2xl border border-accent bg-glass-strong backdrop-blur-2xl shadow-xl-dark animate-fade-in">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-7">
          <div className="relative mb-5">
            <img src="/quazi-logo.svg" alt="QUAZI Logo" className="w-[72px] h-auto drop-shadow-[0_0_20px_rgba(33,158,188,0.7)]" />
            <Sparkles className="absolute -top-2 -right-2 text-brand-light animate-pulse" size={24} />
          </div>
          <h1 className="font-display text-5xl font-bold tracking-[0.15em] mb-2.5 leading-tight gradient-text">QUAZI</h1>
          <p className="text-lg text-secondary font-medium tracking-wider uppercase mb-4 leading-normal">{t('resetPassword')}</p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand/20 border border-brand rounded-xl text-brand-light text-[0.6875rem] font-semibold uppercase tracking-wider">
            <Lock size={14} />
            <span>GSI Secure System</span>
          </div>
        </div>

        {step === 1 ? (
          <form onSubmit={handleSendCode} className="flex flex-col gap-3.5">
            <p className="text-[0.9375rem] text-secondary text-center mb-6 leading-relaxed">{t('enterEmailForReset')}</p>

            <div className="relative flex items-center">
              <Mail size={20} className="absolute left-5 text-tertiary pointer-events-none z-10" />
              <input
                type="email"
                placeholder={t('emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full py-4 pr-4 pl-[3.25rem] bg-surface-elevated border border-medium rounded-xl text-content text-lg font-normal transition-all duration-250 placeholder:text-tertiary focus:outline-none focus:border-brand focus:bg-surface-secondary focus:shadow-[0_0_0_3px_rgba(33,158,188,0.1)]"
                required
                autoComplete="email"
              />
            </div>

            {error && (
              <div className="p-4 bg-status-error/10 border border-status-error rounded-xl text-status-error-light text-base font-medium text-center leading-normal animate-fade-in">
                {error}
              </div>
            )}

            {success && (
              <div className="p-4 bg-green-500/10 border border-green-500 rounded-xl text-green-400 text-base font-medium text-center leading-normal animate-fade-in">
                {success}
              </div>
            )}

            <button
              type="submit"
              className="relative flex items-center justify-center gap-3 w-full py-4 px-6 bg-gradient-to-br from-brand to-brand-hover border-none rounded-xl text-content-inverse text-lg font-semibold cursor-pointer uppercase tracking-widest shadow-glow mt-4 overflow-hidden transition-all duration-250 hover:translate-y-[-3px] hover:shadow-glow-strong active:translate-y-[-1px] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 group"
              disabled={loading}
            >
              <span className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-[left] duration-500 group-hover:left-full" />
              {loading ? (
                <>
                  <span className="w-5 h-5 border-2 border-content/30 border-t-content rounded-full animate-spin" />
                  {t('sending')}
                </>
              ) : (
                t('sendResetCode')
              )}
            </button>

            <button
              type="button"
              className="flex items-center justify-center gap-2 w-full py-3.5 px-6 bg-transparent border border-medium rounded-xl text-secondary text-base font-medium cursor-pointer uppercase tracking-wider mt-3 transition-all duration-250 hover:bg-surface-elevated hover:border-brand hover:text-primary"
              onClick={onBack}
            >
              <ArrowLeft size={18} />
              {t('backToLogin')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="flex flex-col gap-3.5">
            <p className="text-[0.9375rem] text-secondary text-center mb-6 leading-relaxed">{t('enterCodeAndNewPassword')}</p>

            <div className="relative flex items-center">
              <KeyRound size={20} className="absolute left-5 text-tertiary pointer-events-none z-10" />
              <input
                type="text"
                placeholder={t('verificationCode')}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full py-4 pr-4 pl-[3.25rem] bg-surface-elevated border border-medium rounded-xl text-content text-lg font-normal transition-all duration-250 placeholder:text-tertiary focus:outline-none focus:border-brand focus:bg-surface-secondary focus:shadow-[0_0_0_3px_rgba(33,158,188,0.1)]"
                required
                autoComplete="off"
              />
            </div>

            <div className="relative flex items-center">
              <Lock size={20} className="absolute left-5 text-tertiary pointer-events-none z-10" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder={t('newPassword')}
                value={newPassword}
                onChange={(e) => handlePasswordChange(e.target.value)}
                onKeyDown={handleKeyPress}
                onKeyUp={handleKeyPress}
                className="w-full py-4 pr-12 pl-[3.25rem] bg-surface-elevated border border-medium rounded-xl text-content text-lg font-normal transition-all duration-250 placeholder:text-tertiary focus:outline-none focus:border-brand focus:bg-surface-secondary focus:shadow-[0_0_0_3px_rgba(33,158,188,0.1)]"
                required
                autoComplete="new-password"
              />
              {capsLockOn && (
                <div className="absolute top-1/2 right-14 -translate-y-1/2 bg-status-error/10 border border-status-error rounded-lg px-2.5 py-1.5 text-xs text-status-error-light font-medium whitespace-nowrap z-10 animate-fade-in">
                  Caps Lock
                </div>
              )}
              <button
                type="button"
                className="absolute right-5 text-tertiary cursor-pointer z-10 p-1 transition-colors duration-250 hover:text-primary"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {newPassword && (
              <div className="-mt-1 mb-2">
                <div className="text-[0.8125rem] text-tertiary mb-1.5 flex items-center gap-2">
                  {t('passwordStrength')}
                  <span className={`font-semibold ${strengthTextColors[passwordStrength]}`}>
                    {t(passwordStrength)}
                  </span>
                </div>
                <div className="h-1 bg-surface-elevated rounded-sm overflow-hidden">
                  <div className={`h-full transition-all duration-300 rounded-sm ${strengthWidths[passwordStrength]} ${strengthColors[passwordStrength]}`} />
                </div>
              </div>
            )}

            <div className="relative flex items-center">
              <Lock size={20} className="absolute left-5 text-tertiary pointer-events-none z-10" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder={t('confirmNewPassword')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={handleKeyPress}
                onKeyUp={handleKeyPress}
                className="w-full py-4 pr-12 pl-[3.25rem] bg-surface-elevated border border-medium rounded-xl text-content text-lg font-normal transition-all duration-250 placeholder:text-tertiary focus:outline-none focus:border-brand focus:bg-surface-secondary focus:shadow-[0_0_0_3px_rgba(33,158,188,0.1)]"
                required
                autoComplete="new-password"
              />
              {capsLockOn && (
                <div className="absolute top-1/2 right-14 -translate-y-1/2 bg-status-error/10 border border-status-error rounded-lg px-2.5 py-1.5 text-xs text-status-error-light font-medium whitespace-nowrap z-10 animate-fade-in">
                  Caps Lock
                </div>
              )}
              <button
                type="button"
                className="absolute right-5 text-tertiary cursor-pointer z-10 p-1 transition-colors duration-250 hover:text-primary"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <p className="text-[0.8125rem] text-tertiary text-center my-2 leading-relaxed italic">{t('passwordRequirements')}</p>

            {error && (
              <div className="p-4 bg-status-error/10 border border-status-error rounded-xl text-status-error-light text-base font-medium text-center leading-normal animate-fade-in">
                {error}
              </div>
            )}

            {success && (
              <div className="p-4 bg-green-500/10 border border-green-500 rounded-xl text-green-400 text-base font-medium text-center leading-normal animate-fade-in">
                {success}
              </div>
            )}

            <button
              type="submit"
              className="relative flex items-center justify-center gap-3 w-full py-4 px-6 bg-gradient-to-br from-brand to-brand-hover border-none rounded-xl text-content-inverse text-lg font-semibold cursor-pointer uppercase tracking-widest shadow-glow mt-4 overflow-hidden transition-all duration-250 hover:translate-y-[-3px] hover:shadow-glow-strong active:translate-y-[-1px] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 group"
              disabled={loading}
            >
              <span className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-[left] duration-500 group-hover:left-full" />
              {loading ? (
                <>
                  <span className="w-5 h-5 border-2 border-content/30 border-t-content rounded-full animate-spin" />
                  {t('resetting')}
                </>
              ) : (
                t('resetPassword')
              )}
            </button>

            <button
              type="button"
              className="flex items-center justify-center gap-2 w-full py-3.5 px-6 bg-transparent border border-medium rounded-xl text-secondary text-base font-medium cursor-pointer uppercase tracking-wider mt-3 transition-all duration-250 hover:bg-surface-elevated hover:border-brand hover:text-primary"
              onClick={() => setStep(1)}
            >
              <ArrowLeft size={18} />
              {t('backToEmail')}
            </button>
          </form>
        )}

        <div className="mt-4 text-center pt-4 border-t border-subtle">
          <p className="text-[0.6875rem] text-secondary font-medium m-0">🇲🇽 A product of GSI Mexico</p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
